/**
 * A refused org switch, and the words the person sees for it (KAN-172).
 *
 * POST /api/auth/switch-org answers a refusal with the API's error envelope,
 * `{ error, code, message }`. `message` is English. For the refusals the web knows by `code`, it
 * shows its own translated sentence, so a Spanish-speaking parent reads Spanish; for anything else
 * it falls back to the server's message, and only then to a generic line.
 */

/** Thrown by AuthContext.switchOrg: the server's message, plus its machine code when it sent one. */
export class SwitchOrgError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'SwitchOrgError';
    this.code = code;
  }
}

/** Server codes the web words itself, mapped to their translation keys. */
const TRANSLATED_CODES: Readonly<Record<string, string>> = {
  // The membership holds no role Kantera recognises (routes/org-switcher.ts).
  ORG_SWITCH_NO_ROLE: 'webTopBar.switchOrgNoRole',
};

/** What to show for a failed switch: the translated sentence, the server's message, or a generic line. */
export function switchOrgFailureMessage(err: unknown, t: (key: string) => string): string {
  const code = (err as { code?: unknown } | null)?.code;
  const key = typeof code === 'string' ? TRANSLATED_CODES[code] : undefined;
  if (key) return t(key);
  if (err instanceof Error && err.message) return err.message;
  return t('webTopBar.switchOrgFailed');
}
